import type { TourStep } from '@/components/ui/Tour';
import {
  STUDENTS_MANAGEMENT_TOUR_STEP_IDS,
  TOUR_CENTERED_STEP_SELECTOR,
  TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
} from '../tour-constants';
import { TourStepContent } from './StepContent';

export interface StudentsListTourOptions {
  onCloseOverlays?: () => void;
  onOpenAddStudent?: () => void;
  onCloseAddStudent?: () => void;
  onOpenImport?: () => void;
  onCloseImport?: () => void;
  onFinish?: () => void;
}

const openAddStudentDrawer = (options: StudentsListTourOptions) => () => {
  options.onCloseImport?.();
  options.onOpenAddStudent?.();
};

const openImportModal = (options: StudentsListTourOptions) => () => {
  options.onCloseAddStudent?.();
  options.onOpenImport?.();
};

function manualStudentDrawerStep(
  onStepEnter?: () => void,
): Pick<TourStep, 'onStepEnter' | 'targetPollTimeoutMs' | 'waitForPanelReady'> {
  return {
    onStepEnter,
    targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
    waitForPanelReady: true,
  };
}

function getManualStudentFormTourSteps(options: StudentsListTourOptions): TourStep[] {
  const drawerStep = manualStudentDrawerStep(openAddStudentDrawer(options));

  return [
    {
      content: (
        <TourStepContent
          title="Add student form"
          description="Enter roster details here. Leave invite email blank for internal tracking only, or add an email when the guardian or adult student should finish signup online."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.MANUAL_FORM_DRAWER,
      position: 'left',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Student details"
          description="Name, gender, date of birth, skill level, and optional invite email. Required fields are marked on the form."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.MANUAL_FORM_DETAILS,
      position: 'bottom',
      ...drawerStep,
    },
    {
      content: (
        <TourStepContent
          title="Save the student"
          description="Create the student to add them to your roster. You can close without saving to continue the tour."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.MANUAL_FORM_SUBMIT,
      position: 'top',
      ...drawerStep,
    },
  ];
}

export function getStudentsListTourSteps(options: StudentsListTourOptions = {}): TourStep[] {
  return [
    {
      content: (
        <TourStepContent
          title="Managing students"
          description="This is your academy roster. Students are added when families book or register on Professional+, when you enroll them from lessons, and you can also add players manually or import a CSV for bulk setup."
        />
      ),
      selectorId: TOUR_CENTERED_STEP_SELECTOR,
      onStepEnter: () => options.onCloseOverlays?.(),
    },
    {
      content: (
        <TourStepContent
          title="Find students"
          description="Search by name and open Filters to narrow by skill, program, age group, or a specific class session."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.FILTERS,
      position: 'bottom',
      onStepEnter: () => options.onCloseOverlays?.(),
    },
    {
      content: (
        <TourStepContent
          title="Your student roster"
          description="Open a student for attendance, progress, lessons, and notes. Select rows to add students to a lesson or send a notification in bulk."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.LIST,
      position: 'top',
      onStepEnter: () => options.onCloseOverlays?.(),
    },
    {
      content: (
        <TourStepContent
          title="Add a student manually"
          description="Use Add student when someone is not on the roster yet — for walk-ins, paper enrolments, or staff-added players before they book online."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.ADD_STUDENT,
      position: 'bottom',
      onStepEnter: () => options.onCloseOverlays?.(),
    },
    ...getManualStudentFormTourSteps(options),
    {
      content: (
        <TourStepContent
          title="Roster data actions"
          description="Open this menu for attendance CSV export, roster export, and bulk student import."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DATA_ACTIONS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseAddStudent?.();
        options.onCloseImport?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Import students"
          description="Upload a CSV to add many students at once. Required columns are first name, last name, and gender — optional columns cover guardians, invites, skill level, and emergency contacts."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.IMPORT_MODAL,
      position: 'left',
      onStepEnter: openImportModal(options),
      targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
      waitForPanelReady: true,
    },
  ];
}

/** Default steps for registry anchor checks and tests. */
export const studentsListTourSteps: TourStep[] = getStudentsListTourSteps();

export interface StudentDetailTourContext {
  hasPathwayTab: boolean;
  hasCreditsTab: boolean;
}

function buildStudentDetailTabsDescription(ctx: StudentDetailTourContext): string {
  const sections = [
    'Profile — roster details, emergency contacts, siblings, and medical notes.',
    'Attendance — session-by-session history with export for records.',
    'Progress — skill development tracked over time.',
  ];
  if (ctx.hasPathwayTab) {
    sections.push(
      'Pathway — program skill assessments when the student is enrolled in development-program lessons.',
    );
  }
  sections.push(
    'Lessons — current enrollments, booking details, and add-to-lesson actions.',
  );
  if (ctx.hasCreditsTab) {
    sections.push('Credits — available, used, and expired make-up credits.');
  }
  sections.push('Notes — coach observations and internal notes for staff.');
  return sections.join(' ');
}

export function getStudentDetailTourSteps(ctx: StudentDetailTourContext): TourStep[] {
  return [
    {
      content: (
        <TourStepContent
          title="Student sections"
          description={buildStudentDetailTabsDescription(ctx)}
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_TABS,
      position: 'bottom',
    },
    {
      content: (
        <TourStepContent
          title="Basic information"
          description="Name, age, gender, skill level, and active status. Use Edit student details to update the roster, Send notification for a one-off message, or Send signup invite when an invite email is on file."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_BASIC_INFO,
      position: 'right',
    },
    {
      content: (
        <TourStepContent
          title="Emergency and booking contact"
          description="Emergency contact details for incidents on court. Below that, the linked guardian booking account, pending invite email, and SMS opt-in status when your academy uses SMS."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_CONTACT,
      position: 'left',
    },
    {
      content: (
        <TourStepContent
          title="Siblings in this academy"
          description="Other students sharing the same guardian booking account — useful for sibling discounts and seeing who else in the family is enrolled."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_SIBLINGS,
      position: 'top',
    },
    {
      content: (
        <TourStepContent
          title="Medical notes"
          description="Allergies, conditions, or care instructions coaches and staff should know before sessions. Edit the student to add or update notes."
        />
      ),
      selectorId: STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_MEDICAL,
      position: 'top',
    },
  ];
}

/** Default registry steps (pathway + credits visible) for anchor readiness checks. */
export const studentDetailTourSteps: TourStep[] = getStudentDetailTourSteps({
  hasPathwayTab: true,
  hasCreditsTab: true,
});
